import { memo, useCallback, useMemo } from 'react';

import { GapHorizontalIcon } from '@/assets/icons';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { HighwayArea } from '@thive/fddm-types';
import { MIN_HIGHWAY_THICKNESS } from '@/modules/floorplanDesign/constants/areas';
import { NumUtils } from '@/modules/common/utils';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';

interface Props {
  selectedHighwayAreas: HighwayArea[];
}

export const ThicknessComponent = ({ selectedHighwayAreas: selectedHighways }: Props) => {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const value = useMemo(() => {
    const values = selectedHighways
      .map((highway) =>
        NumUtils.metersToMmRounded(
          highway.segments[0].leftSideWidth + highway.segments[0].rightSideWidth,
        ),
      )
      .filter((value): value is number => value !== undefined);

    return getCommonNumber(values);
  }, [selectedHighways]);

  const onChange = useCallback(
    (newValueMm: number) => {
      if(isNaN(newValueMm)) return;
      const newValue = NumUtils.mmToMeters(newValueMm);
      const updatedAreas = createEmptyAreas();
      selectedHighways.forEach((highway) => {
        pushAreaToItsCollection(updatedAreas, {
          ...highway,
          segments: minItemsArrayUtils.mapAtLeast1(highway.segments, (segment) => ({
            ...segment,
            leftSideWidth: newValue / 2,
            rightSideWidth: newValue / 2,
          })),
        });
      });

      fddmSvc.setPreference('highwayArea.segment.width', newValue);
      fddmSvc.updateAreas(updatedAreas);
    },
    [selectedHighways, fddmSvc],
  );

  return (
    <PropertiesLabel i18nkey='interface:properties.highway.width'>
      <NumberInput
        value={value}
        unit='mm'
        prefix={<GapHorizontalIcon />}
        sx={{ width: '100%' }}
        minValue={NumUtils.metersToMmRounded(MIN_HIGHWAY_THICKNESS)}
        onChange={onChange}
      />
    </PropertiesLabel>
  );
};

export const Thickness = memo(ThicknessComponent);
